import React, { useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  ChevronDown,
  Copy,
  TrendingUp,
  ShieldCheck,
  Mail,
  Gauge,
  Code2,
  BarChart3,
  Rocket,
  BookOpen,
  Terminal,
  FlaskConical,
  Newspaper,
} from "lucide-react";
import "./SG3002.scss";
// ============================================================================
// DATA
// ============================================================================
import customerSendingPhoto from "../../../../../assets/Images/authentic-small-youthful-marketing-agency.jpg";
type Cycle = "monthly" | "annual";

interface Tier {
  key: string;
  name: string;
  blurb: string;
  volume: string;
  monthly: number;
  annual: number;
  overage: string;
  cta: string;
  highlighted?: boolean;
  features: string[];
}

const TIERS: Tier[] = [
  {
    key: "essentials",
    name: "Essentials",
    blurb: "For startups sending their first transactional emails",
    volume: "50,000",
    monthly: 19,
    annual: 15,
    overage: "$1.00 / 1,000 emails after your plan limit",
    cta: "Start for free",
    features: [
      "Transactional + marketing sends",
      "Single sender authentication",
      "Email activity feed",
      "Community support",
    ],
  },
  {
    key: "pro",
    name: "Pro",
    blurb: "For growing products that need deliverability and scale",
    volume: "1,500,000",
    monthly: 89,
    annual: 71,
    overage: "$0.80 / 1,000 emails after your plan limit",
    cta: "Start for free",
    highlighted: true,
    features: [
      "Domain authentication (SPF/DKIM/DMARC)",
      "Real-time webhooks & event tracking",
      "A/B testing & send-time optimization",
      "99.9% delivery uptime",
      "Priority support",
      "Dedicated IPs available as add-on",
    ],
  },
  {
    key: "enterprise",
    name: "Enterprise",
    blurb: "For high-volume senders with strict compliance needs",
    volume: "Custom",
    monthly: 0,
    annual: 0,
    overage: "Volume-based contract pricing, billed by usage",
    cta: "Talk to sales",
    features: [
      "Unlimited send volume",
      "Dedicated IP pools included",
      "Subuser management & SSO",
      "Dedicated deliverability manager",
      "99.95% uptime SLA",
      "Custom integrations & API limits",
    ],
  },
];

const FEATURES = [
  {
    icon: Code2,
    photo: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&q=80&auto=format&fit=crop",
    title: "One API for every email you send",
    body: "A single REST and SMTP integration handles transactional receipts, password resets, and marketing campaigns — no juggling separate tools or providers as you grow.",
  },
  {
    icon: Gauge,
    photo: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80&auto=format&fit=crop",
    title: "Deliverability that's handled for you",
    body: "Domain authentication, IP warmup, and reputation monitoring run automatically, so emails land in the inbox instead of getting filtered before anyone sees them.",
  },
  {
    icon: BarChart3,
    photo: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80&auto=format&fit=crop",
    title: "Visibility into every send",
    body: "Opens, clicks, bounces, and spam reports stream into a live activity feed and webhooks, so you know what happened to an email the moment it happens.",
  },
];

const USE_CASES = [
  {
    key: "alerts",
    label: "Alerts and notifications",
    heading: "Alerts and notifications",
    body:
      "Reach the inbox with Swift Grid's industry-leading deliverability rates. Maximize every transactional send with dedicated IPs, custom domains, and expert support.",
    proof:
      "A fast-growing checkout platform uses Swift Grid's Email API to send payment and shipping alerts to hundreds of thousands of customers.",
    stats: [
      { value: "99.5%", label: "average delivery rate" },
      { value: "91.3%", label: "inbox placement rate" },
      { value: "340K+", label: "merchants sending alerts" },
    ],
    cta: "Read the case study",
    photo: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=900&q=80&auto=format&fit=crop",
  },
  {
    key: "marketing",
    label: "Marketing and promotions",
    heading: "Marketing and promotions",
    body:
      "Automate your email marketing to save time and drive better results. Build high-impact automations with simple triggers and a centralized view of every campaign.",
    proof:
      "One community marketplace saw email volume grow 50% faster than its membership base, and needed a platform that could scale without dropping delivery rate.",
    stats: [
      { value: "99%", label: "delivery rate" },
      { value: "95%", label: "reduction in latency" },
      { value: "<1", label: "engineer managing email" },
    ],
    cta: "Read the case study",
    photo: "https://images.unsplash.com/photo-1557200134-90327ee9fafa?w=900&q=80&auto=format&fit=crop",
  },
  {
    key: "verification",
    label: "Verifications and identity",
    heading: "Verifications and identity",
    body:
      "Use Swift Grid's real-time and bulk Email Address Validation to improve subscriber list quality. Identify and remove invalid or non-deliverable addresses before you send, to clean your list and cut bounce rate.",
    proof: null,
    stats: [
      { value: "0.00–1", label: "validity scores" },
      { value: "350", label: "validations per second" },
    ],
    cta: "Learn about Email Validation",
    photo: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=900&q=80&auto=format&fit=crop",
  },
];

const STORIES = [
  {
    company: "Marlowe & Voss",
    industry: "Recruiting SaaS",
    metric: "Inbox placement up to 98%",
    quote:
      "We moved off a self-hosted mail server in an afternoon. Domain authentication and IP warmup were handled for us, and placement improved within the first week.",
    person: "Head of Engineering",
  },
  {
    company: "Aldergate Partners",
    industry: "Fintech",
    metric: "1.2M transactional emails / mo",
    quote:
      "Receipts, statements, and account alerts all run through one API now. The activity feed alone has cut our support tickets about 'missing' emails by half.",
    person: "VP of Platform",
  },
  {
    company: "Brightwell Clinic Group",
    industry: "Healthcare",
    metric: "Zero deliverability incidents in 12 months",
    quote:
      "Appointment confirmations are critical for us — a missed email means a missed visit. We haven't had a single deliverability incident since switching.",
    person: "IT Director",
  },
];

const FAQS = [
  {
    q: "Do I need to run my own mail server?",
    a: "No. Swift Grid handles sending infrastructure, IP reputation, and domain authentication for you — you integrate the API or SMTP relay and start sending.",
  },
  {
    q: "Can I send both transactional and marketing email?",
    a: "Yes. Transactional sends (receipts, resets, alerts) and marketing campaigns run through the same API and account, with separate reputation and reporting.",
  },
  {
    q: "How is deliverability protected?",
    a: "Domain authentication (SPF/DKIM/DMARC), automated IP warmup, and ongoing reputation monitoring run by default. Pro and Enterprise plans add dedicated IPs.",
  },
  {
    q: "Is sending data and email content secure?",
    a: "Data is encrypted in transit and at rest, with SOC 2 Type II compliance. Enterprise plans add SSO, subuser access controls, and dedicated infrastructure.",
  },
];

// ----------------------------------------------------------------------------
// Code samples — all target the real Swift Grid send endpoint:
// POST https://swiftgrid.cilbox.com/api/swiftgrid/domains/send
// ----------------------------------------------------------------------------
const CODE_SAMPLES = [
  {
    lang: "Node.js",
    filename: "send.js",
    code: `// Send an email through the Swift Grid REST API
const response = await fetch(
  "https://swiftgrid.cilbox.com/api/swiftgrid/domains/send",
  {
    method: "POST",
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      from: "billing@yourdomain.com",
      to: ["customer@example.com"],
      subject: "Your receipt from Acme Co.",
      htmlBody: "<strong>Thanks for your purchase!</strong>",
      textBody: "Thanks for your purchase!",
      fromName: "Acme Billing",
      cc: [],
      bcc: [],
      sandboxMode: false,
      sendAt: new Date().toISOString(),
    }),
  }
);

const data = await response.json();
console.log(data);`,
  },
  {
    lang: "Python",
    filename: "send.py",
    code: `import requests
from datetime import datetime, timezone

response = requests.post(
    "https://swiftgrid.cilbox.com/api/swiftgrid/domains/send",
    headers={
        "Accept": "application/json",
        "Content-Type": "application/json",
    },
    json={
        "from": "billing@yourdomain.com",
        "to": ["customer@example.com"],
        "subject": "Your receipt from Acme Co.",
        "htmlBody": "<strong>Thanks for your purchase!</strong>",
        "textBody": "Thanks for your purchase!",
        "fromName": "Acme Billing",
        "cc": [],
        "bcc": [],
        "sandboxMode": False,
        "sendAt": datetime.now(timezone.utc).isoformat(),
    },
)

print(response.status_code, response.json())`,
  },
  {
    lang: "cURL",
    filename: "send.sh",
    code: `curl -X 'POST' \\
  'https://swiftgrid.cilbox.com/api/swiftgrid/domains/send' \\
  -H 'accept: application/json' \\
  -H 'Content-Type: application/json' \\
  -d '{
  "from": "billing@yourdomain.com",
  "to": [
    "customer@example.com"
  ],
  "subject": "Your receipt from Acme Co.",
  "htmlBody": "<strong>Thanks for your purchase!</strong>",
  "textBody": "Thanks for your purchase!",
  "fromName": "Acme Billing",
  "cc": [],
  "bcc": [],
  "sandboxMode": true,
  "sendAt": "2026-09-15T23:21:20.511Z"
}'`,
  },
  {
    lang: "Ruby",
    filename: "send.rb",
    code: `require "net/http"
require "json"
require "uri"

uri = URI("https://swiftgrid.cilbox.com/api/swiftgrid/domains/send")
http = Net::HTTP.new(uri.host, uri.port)
http.use_ssl = true

request = Net::HTTP::Post.new(uri, {
  "Accept" => "application/json",
  "Content-Type" => "application/json",
})

request.body = {
  from: "billing@yourdomain.com",
  to: ["customer@example.com"],
  subject: "Your receipt from Acme Co.",
  htmlBody: "<strong>Thanks for your purchase!</strong>",
  textBody: "Thanks for your purchase!",
  fromName: "Acme Billing",
  cc: [],
  bcc: [],
  sandboxMode: false,
  sendAt: Time.now.utc.iso8601,
}.to_json

response = http.request(request)
puts response.body`,
  },
  {
    lang: "Go",
    filename: "send.go",
    code: `package main

import (
	"bytes"
	"encoding/json"
	"fmt"
	"net/http"
	"time"
)

type SendRequest struct {
	From        string   \`json:"from"\`
	To          []string \`json:"to"\`
	Subject     string   \`json:"subject"\`
	HTMLBody    string   \`json:"htmlBody"\`
	TextBody    string   \`json:"textBody"\`
	FromName    string   \`json:"fromName"\`
	CC          []string \`json:"cc"\`
	BCC         []string \`json:"bcc"\`
	SandboxMode bool     \`json:"sandboxMode"\`
	SendAt      string   \`json:"sendAt"\`
}

func main() {
	payload := SendRequest{
		From:        "billing@yourdomain.com",
		To:          []string{"customer@example.com"},
		Subject:     "Your receipt from Acme Co.",
		HTMLBody:    "<strong>Thanks for your purchase!</strong>",
		TextBody:    "Thanks for your purchase!",
		FromName:    "Acme Billing",
		SandboxMode: false,
		SendAt:      time.Now().UTC().Format(time.RFC3339),
	}

	body, _ := json.Marshal(payload)
	url := "https://swiftgrid.cilbox.com/api/swiftgrid/domains/send"
	req, _ := http.NewRequest("POST", url, bytes.NewBuffer(body))
	req.Header.Set("Accept", "application/json")
	req.Header.Set("Content-Type", "application/json")

	client := &http.Client{}
	resp, err := client.Do(req)
	if err != nil {
		panic(err)
	}
	defer resp.Body.Close()
	fmt.Println(resp.StatusCode)
}`,
  },
];

const INTEGRATION_LINKS = [
  {
    icon: Rocket,
    label: "Quickstart documentation",
    desc: "Send your first email in under five minutes",
  },
  {
    icon: Terminal,
    label: "Code samples & quick deploy",
    desc: "Copy-paste examples in every major language",
  },
  {
    icon: BookOpen,
    label: "API reference",
    desc: "Every endpoint, parameter, and response object",
  },
  {
    icon: FlaskConical,
    label: "Interactive documentation",
    desc: "Test live requests directly from your browser",
  },
  {
    icon: Newspaper,
    label: "Blog articles",
    desc: "Deliverability guides and engineering deep-dives",
  },
];

// ----------------------------------------------------------------------------
// Minimal syntax highlighter — enough to make the code panel read like a
// real editor without pulling in a full highlighting library.
// ----------------------------------------------------------------------------
const TOKEN_REGEX =
  /(\/\/[^\n]*|#(?!\{)[^\n]*)|("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`)|\b(const|let|var|function|async|await|return|import|require|from|def|package|type|struct|func|True|False|None|true|false|null|nil|require_relative|include|puts|print|panic)\b|\b(\d+\.?\d+|\d+)\b/g;

function highlightLine(line: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  TOKEN_REGEX.lastIndex = 0;
  while ((match = TOKEN_REGEX.exec(line)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(line.slice(lastIndex, match.index));
    }
    const [full, comment, str, keyword, num] = match;
    let cls = "";
    if (comment) cls = "tok-comment";
    else if (str) cls = "tok-string";
    else if (keyword) cls = "tok-keyword";
    else if (num) cls = "tok-number";
    nodes.push(
      <span className={cls} key={`${keyPrefix}-${i++}`}>
        {full}
      </span>
    );
    lastIndex = match.index + full.length;
  }
  if (lastIndex < line.length) nodes.push(line.slice(lastIndex));
  return nodes;
}

// ============================================================================
// COMPONENT
// ============================================================================
const SG3002: React.FC = () => {
  const [cycle, setCycle] = useState<Cycle>("monthly");
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [activeUseCase, setActiveUseCase] = useState(0);
  const [activeLang, setActiveLang] = useState(0);
  const [copied, setCopied] = useState(false);
  const uc = USE_CASES[activeUseCase];
  const activeSample = CODE_SAMPLES[activeLang];

  // Every button on this page routes here.
  const SIGNUP_URL = "/auth/signup";
  const goToSignup = () => {
    window.location.href = SIGNUP_URL;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(activeSample.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard API unavailable — fail silently
    }
  };

  return (
    <div className="wrap">
      {/* HERO — dark ink, two-column */}
      <section className="hero-dark">
        <div className="hero-dark-inner">
          <div className="hero-dark-copy">
            <span className="eyebrow-pill">Swift Grid Email API</span>
            <h1>Move blazing fast with the Swift Grid Email API</h1>
            <p className="hero-dark-sub">
              Send transactional and marketing email through one API, built
              for teams who don't have the capacity to run their own mail
              infrastructure — just business email, sent reliably, at scale.
            </p>
            <div className="hero-dark-cta">
              <button className="btn-primary" onClick={goToSignup}>
                Start for free <ArrowRight size={16} />
              </button>
              <button className="btn-link-dark" onClick={goToSignup}>View pricing</button>
            </div>
          </div>

          <div className="hero-dark-media">
            <img
              src={customerSendingPhoto}
              alt="Developer working at a desk with multiple monitors"
            />
          </div>
        </div>
      </section>

      {/* INTEGRATION */}
      <section className="section integration-section">
        <div className="section-head">
          <h2>Integrate in minutes, start sending sooner</h2>
          <p className="section-sub">
            Integrate and deliver in minutes with our RESTful API and SMTP,
            libraries for every major language, and interactive documentation.
          </p>
        </div>

        <div className="integration-grid">
          <div className="integration-copy">
            <div className="integration-links">
              {INTEGRATION_LINKS.map((link) => (
                <a
                  href="#"
                  className="integration-link"
                  key={link.label}
                  onClick={(e) => e.preventDefault()}
                >
                  <span className="integration-link-icon">
                    <link.icon size={18} strokeWidth={1.75} />
                  </span>
                  <span className="integration-link-text">
                    <span className="integration-link-label">{link.label}</span>
                    <span className="integration-link-desc">{link.desc}</span>
                  </span>
                  <ArrowUpRight size={16} className="integration-link-arrow" />
                </a>
              ))}
            </div>
            <button className="btn-primary integration-cta" onClick={goToSignup}>
              Start for free <ArrowRight size={16} />
            </button>
          </div>

          {/* Real code-editor style panel */}
          <div className="integration-code">
            <div className="code-window">
              <div className="code-window-bar">
                <span className="code-window-dots">
                  <span className="dot dot-red" />
                  <span className="dot dot-yellow" />
                  <span className="dot dot-green" />
                </span>
                <span className="code-window-filename">{activeSample.filename}</span>
                <button className="code-copy-btn" onClick={handleCopy} type="button">
                  {copied ? <Check size={13} strokeWidth={2.25} /> : <Copy size={13} strokeWidth={2} />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>

              <div className="code-tabs" role="tablist">
                {CODE_SAMPLES.map((s, i) => (
                  <button
                    key={s.lang}
                    role="tab"
                    aria-selected={activeLang === i}
                    className={activeLang === i ? "code-tab active" : "code-tab"}
                    onClick={() => setActiveLang(i)}
                  >
                    {s.lang}
                  </button>
                ))}
              </div>

              <pre className="code-block">
                <code>
                  {activeSample.code.split("\n").map((line, i) => (
                    <div className="code-line" key={i}>
                      <span className="code-line-no">{i + 1}</span>
                      <span className="code-line-content">
                        {line.length ? highlightLine(line, `l${i}`) : "\u00A0"}
                      </span>
                    </div>
                  ))}
                </code>
              </pre>
            </div>
          </div>
        </div>

        <div className="help-strip">
          <h3>Need help building? No problem.</h3>
          <p>
            Build your one-of-a-kind email program with help from our
            integration partners.{" "}
            <a href="#" onClick={(e) => e.preventDefault()}>
              View partners
            </a>
          </p>
        </div>
      </section>

      {/* FEATURES */}
      <section className="section">
        <div className="section-head">
          <span className="eyebrow">Platform</span>
          <h2>Business email, handled end to end</h2>
        </div>
        <div className="feature-grid">
          {FEATURES.map((f) => (
            <div className="feature-card" key={f.title}>
              <div className="feature-photo-wrap">
                <img className="feature-photo" src={f.photo} alt="" />
                <span className="feature-icon">
                  <f.icon size={18} strokeWidth={1.75} />
                </span>
              </div>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* USE CASES — dark tabbed section (Twilio SendGrid style) */}
      <section className="usecases-dark">
        <div className="usecases-dark-inner">
          <h2 className="usecases-title">Ready to handle your critical email workloads</h2>
          <p className="usecases-subtitle">
            Send at scale with the developer-first email platform
          </p>

          <div className="usecases-tabs" role="tablist">
            {USE_CASES.map((u, i) => (
              <button
                key={u.key}
                role="tab"
                aria-selected={activeUseCase === i}
                className={activeUseCase === i ? "usecases-tab active" : "usecases-tab"}
                onClick={() => setActiveUseCase(i)}
              >
                {u.label}
              </button>
            ))}
          </div>

          <div className="usecases-panel">
            <div className="usecases-panel-copy">
              <h3>{uc.heading}</h3>
              <p className="usecases-panel-body">{uc.body}</p>
              <div className="usecases-divider" />
              {uc.proof && <p className="usecases-proof">{uc.proof}</p>}

              <div className="usecases-stats">
                {uc.stats.map((s) => (
                  <div className="usecases-stat" key={s.label}>
                    <div className="usecases-stat-value">{s.value}</div>
                    <div className="usecases-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>

              <button className="btn-secondary usecases-cta" onClick={goToSignup}>
                {uc.cta} <ArrowRight size={15} />
              </button>
            </div>

            <div className="usecases-panel-media">
              <img src={uc.photo} alt="" />
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="section" id="pricing">
        <div className="section-head">
          <span className="eyebrow">Pricing</span>
          <h2>Pay for what you send</h2>
          <p className="section-sub">
            Every plan is built around monthly send volume. All plans include API + SMTP relay, domain authentication, and a live activity feed.
          </p>
          <div className="toggle">
            <button
              className={cycle === "monthly" ? "toggle-btn active" : "toggle-btn"}
              onClick={() => setCycle("monthly")}
            >
              Monthly
            </button>
            <button
              className={cycle === "annual" ? "toggle-btn active" : "toggle-btn"}
              onClick={() => setCycle("annual")}
            >
              Annual <span className="toggle-save">Save 20%</span>
            </button>
          </div>
        </div>

        <div className="pricing-grid">
          {TIERS.map((tier) => (
            <div
              className={tier.highlighted ? "tier tier-highlight" : "tier"}
              key={tier.key}
            >
              {tier.highlighted && <span className="tier-badge">Most popular</span>}
              <h3>{tier.name}</h3>
              <p className="tier-blurb">{tier.blurb}</p>

              <div className="tier-volume">
                <span className="tier-volume-value">{tier.volume}</span>
                <span className="tier-volume-label">emails / month included</span>
              </div>

              <div className="tier-price">
                {tier.monthly === 0 ? (
                  <span className="price-custom">Custom pricing</span>
                ) : (
                  <>
                    <span className="price-amount">
                      ${cycle === "monthly" ? tier.monthly : tier.annual}
                    </span>
                    <span className="price-period">/ mo</span>
                  </>
                )}
              </div>
              <p className="tier-overage">{tier.overage}</p>

              <button
                className={tier.highlighted ? "btn-primary full" : "btn-secondary full"}
                onClick={goToSignup}
              >
                {tier.cta}
              </button>
              <ul className="tier-features">
                {tier.features.map((f) => (
                  <li key={f}>
                    <Check size={15} strokeWidth={2.25} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CLIENT SUCCESS STORIES */}
      <section className="section">
        <div className="section-head">
          <span className="eyebrow">Customer stories</span>
          <h2>Results from teams sending on Swift Grid</h2>
        </div>
        <div className="story-grid">
          {STORIES.map((s) => (
            <div className="story-card" key={s.company}>
              <div className="story-metric">
                <TrendingUp size={16} strokeWidth={2} />
                {s.metric}
              </div>
              <p className="story-quote">&ldquo;{s.quote}&rdquo;</p>
              <div className="story-attrib">
                <div className="story-company">{s.company}</div>
                <div className="story-role">
                  {s.person} &middot; {s.industry}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SECURITY STRIP */}
      <section className="security">
        <div className="security-item">
          <ShieldCheck size={18} strokeWidth={1.75} />
          SOC 2 Type II compliant
        </div>
        <div className="security-item">
          <Mail size={18} strokeWidth={1.75} />
          SPF / DKIM / DMARC by default
        </div>
        <div className="security-item">
          <Gauge size={18} strokeWidth={1.75} />
          99.95% uptime SLA
        </div>
      </section>

      {/* FAQ */}
      <section className="section faq-section">
        <div className="section-head">
          <span className="eyebrow">Questions</span>
          <h2>Frequently asked questions</h2>
        </div>
        <div className="faq-list">
          {FAQS.map((item, i) => (
            <div className="faq-item" key={item.q}>
              <button
                className="faq-q"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                aria-expanded={openFaq === i}
              >
                {item.q}
                <ChevronDown
                  size={18}
                  className={openFaq === i ? "faq-chev open" : "faq-chev"}
                />
              </button>
              {openFaq === i && <p className="faq-a">{item.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="cta-band">
        <h2>Send your first email in under five minutes</h2>
        <p>Free tier included. No credit card required.</p>
        <div className="hero-cta center">
          <button className="btn-primary" onClick={goToSignup}>
            Start for free <ArrowRight size={16} />
          </button>
          <button className="btn-ghost on-dark" onClick={goToSignup}>View pricing</button>
        </div>
      </section>
    </div>
  );
};

export default SG3002;